import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { setBreadcrumbItems } from "../../store/actions";
import { ModernCard, ModernButton } from "../../components/ModernUI";
import axiosInstance from "../../components/Common/ApiManagement/axiosInstance";
import { ApiNames } from "../../components/Common/ApiManagement/apiServices";
import "./Settings.css";
import { Eye, EyeOff, Check, X, Clock, AlertCircle, Monitor, Smartphone } from "lucide-react";

const SETTINGS_STORAGE_KEY = "govcom_settings";

/**
 * Enhanced Settings page with:
 * - Appearance settings (Theme, Language)
 * - Notification settings
 * - Security settings (Change Password, Login History)
 */
const Settings = props => {
  document.title = "Settings | GOVCOM";

  // Notification Settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [reportAlerts, setReportAlerts] = useState(true);

  // Password Change
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Login History
  const [loginHistory, setLoginHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");

  // General
  const [isSaving, setIsSaving] = useState(false);

  // Load user profile for language saving
  const [profileUser, setProfileUser] = useState(null);

  useEffect(() => {
    props.setBreadcrumbItems("Settings", []);

    // Load saved settings from localStorage
    try {
      const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (raw) {
        const stored = JSON.parse(raw);
        if (typeof stored.emailNotifications === "boolean") {
          setEmailNotifications(stored.emailNotifications);
        }
        if (typeof stored.reportAlerts === "boolean") {
          setReportAlerts(stored.reportAlerts);
        }
      }
    } catch {
      // ignore parse errors and fall back to defaults
    }

    // Load login history
    loadLoginHistory();
  }, []);

  const loadLoginHistory = async () => {
    setIsLoadingHistory(true);
    setHistoryError("");
    try {
      const response = await axiosInstance.get(ApiNames.USER_LOGIN_HISTORY);
      if (response.data && response.data.data) {
        setLoginHistory(response.data.data.history || []);
      }
    } catch (error) {
      console.error("Error loading login history:", error);
      // Check if it's a 404 or endpoint not found
      if (error.response && (error.response.status === 404 || error.response.status === 501)) {
        setHistoryError("Login history feature is not yet available");
      } else {
        setHistoryError("Unable to load login history at this time");
      }
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);

    const data = {
      emailNotifications,
      reportAlerts,
    };

    try {
      // Save to localStorage
      window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(data));

    } finally {
      // small delay for nicer UX
      setTimeout(() => setIsSaving(false), 300);
    }
  };



  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data: res } = await axiosInstance.get('user/me');
        const rawUser = res.data;
        setProfileUser(rawUser);
      } catch (error) {
        console.error("Error loading user data:", error);
      }
    };

    fetchUserData();
  }, []);

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
    return requirements;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    const requirements = validatePassword(newPassword);
    if (!Object.values(requirements).every(Boolean)) {
      setPasswordError("Password does not meet security requirements");
      return;
    }

    setIsChangingPassword(true);

    try {
      const response = await axiosInstance.post(ApiNames.USER_CHANGE_PASSWORD, {
        currentPassword,
        newPassword,
        confirmPassword
      });

      if (response.data && response.data.status === 1) {
        setPasswordSuccess("Password changed successfully");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setPasswordError(response.data?.message || "Failed to change password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      setPasswordError(
        error.response?.data?.message || "Failed to change password. Please try again."
      );
    } finally {
      setIsChangingPassword(false);
    }
  };

  const passwordRequirements = newPassword ? validatePassword(newPassword) : null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="settings-page-container">
      <div className="settings-page-content">

        {/* Notification Settings */}
        {/* <ModernCard
          title="Notification Settings"
          subtitle="Manage how you receive updates"
        >
          <div className="profile-info-grid" style={{ marginBottom: 24 }}>
            <div className="profile-info-item">
              <label className="profile-info-label">Email Notifications</label>
              <label className="profile-toggle">
                <input
                  type="checkbox"
                  checked={emailNotifications}
                  onChange={e => setEmailNotifications(e.target.checked)}
                />
                <span className="profile-toggle-slider"></span>
              </label>
              <div className="profile-info-helper">
                Receive important updates about scans and account activity.
              </div>
            </div>

            <div className="profile-info-item">
              <label className="profile-info-label">Report Alerts</label>
              <label className="profile-toggle">
                <input
                  type="checkbox"
                  checked={reportAlerts}
                  onChange={e => setReportAlerts(e.target.checked)}
                />
                <span className="profile-toggle-slider"></span>
              </label>
              <div className="profile-info-helper">
                Get notified when new compliance reports are available.
              </div>
            </div>
          </div>

          <div className="profile-actions">
            <ModernButton
              variant="primary"
              size="md"
              onClick={handleSaveSettings}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Settings"}
            </ModernButton>
          </div>
        </ModernCard> */}

        {/* Security - Change Password */}
        <ModernCard
          title="Change Password"
          subtitle="Update your account password"
        >
          {passwordError && (
            <div className="error-message">
              <AlertCircle size={16} style={{ display: 'inline', marginRight: 8 }} />
              {passwordError}
            </div>
          )}
          {passwordSuccess && (
            <div className="success-message">
              <Check size={16} style={{ display: 'inline', marginRight: 8 }} />
              {passwordSuccess}
            </div>
          )}

          <form onSubmit={handleChangePassword} className="password-form">
            <div className="password-input-group">
              <label className="profile-info-label">Current Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  className="profile-input"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="password-input-group">
              <label className="profile-info-label">New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showNewPassword ? "text" : "password"}
                  className="profile-input"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {passwordRequirements && (
                <div className="password-requirements">
                  Password must contain:
                  <ul>
                    <li className={passwordRequirements.length ? "met" : "unmet"}>
                      {passwordRequirements.length ? <Check size={12} /> : <X size={12} />} At least 8 characters
                    </li>
                    <li className={passwordRequirements.uppercase ? "met" : "unmet"}>
                      {passwordRequirements.uppercase ? <Check size={12} /> : <X size={12} />} One uppercase letter
                    </li>
                    <li className={passwordRequirements.lowercase ? "met" : "unmet"}>
                      {passwordRequirements.lowercase ? <Check size={12} /> : <X size={12} />} One lowercase letter
                    </li>
                    <li className={passwordRequirements.number ? "met" : "unmet"}>
                      {passwordRequirements.number ? <Check size={12} /> : <X size={12} />} One number
                    </li>
                    <li className={passwordRequirements.special ? "met" : "unmet"}>
                      {passwordRequirements.special ? <Check size={12} /> : <X size={12} />} One special character
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div className="password-input-group">
              <label className="profile-info-label">Confirm New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="profile-input"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="profile-actions">
              <ModernButton
                variant="primary"
                size="md"
                type="submit"
                disabled={isChangingPassword}
              >
                {isChangingPassword ? "Changing Password..." : "Change Password"}
              </ModernButton>
            </div>
          </form>
        </ModernCard>

        {/* Login History */}
        <ModernCard
          title="Login History"
          subtitle="Recent login activity on your account"
        >
          {isLoadingHistory ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          ) : historyError ? (
            <div className="error-message">{historyError}</div>
          ) : loginHistory.length === 0 ? (
            <div className="empty-state">
              <Clock className="empty-state-icon" />
              <div className="empty-state-title">No login history</div>
              <div className="empty-state-description">
                Your recent login activity will appear here
              </div>
            </div>
          ) : (
            <div className="login-history-container">
              <table className="login-history-table">
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>Device</th>
                    {/* <th>Location</th> */}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loginHistory.map((entry, index) => (
                    <tr key={index}>
                      <td data-label="Date & Time">{formatDate(entry.loginTime)}</td>
                      <td data-label="Device">
                        <div className="device-info">
                          <div className="device-name">
                            {entry.device === 'Desktop' ? <Monitor size={14} /> : <Smartphone size={14} />}
                            {' '}{entry.device}
                          </div>
                          <div className="browser-name">{entry.browser}</div>
                        </div>
                      </td>
                      {/* <td data-label="Location">
                        <div className="ip-address">{entry.ipAddress}</div>
                      </td> */}
                      <td data-label="Status">
                        <span className={`login-status ${entry.status}`}>
                          {entry.status === 'success' ? (
                            <><Check className="login-status-icon" /> Success</>
                          ) : entry.status === 'logout' ? (
                            <><Clock className="login-status-icon" /> Logout</>
                          ) : (
                            <><X className="login-status-icon" /> Failed</>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModernCard>
      </div>
    </div>
  );
};

export default connect(null, { setBreadcrumbItems })(Settings);
